import React, { useState } from 'react';
import { Link } from '@mui/material';
import { useGlobalization } from '@Translations/useGlobalization';
import { NotificationBanner } from '@Components/NotificationBanner/NotificationBanner.component';
import { ComponentAvailability } from '@Types/riskTemplateApply.types';
import { dismissMissingSummary } from '@Utils/templateBanner.storage';

interface Props {
	section: 'lof' | 'cof';
	unavailableComponents: ComponentAvailability[];
	unrecognisedValues: { componentName: string; values: string[] }[];
	// Data points in the config still without a score (see countValuesNeedingScore).
	valuesNeedingScore: number;
	onContinue: () => void;
	onReview: () => void;
	onDismiss: () => void;
	configId: string;
}

// Warning banner shown after template apply when some components cannot be
// supported by the tenant's data and/or have values that could not be matched
// to the template score table. Scoped to LoF or CoF for the active setup tab.
export const MissingComponentsBanner = ({
	section,
	unavailableComponents,
	unrecognisedValues,
	valuesNeedingScore,
	onContinue,
	onReview,
	onDismiss,
	configId,
}: Props) => {
	const { t } = useGlobalization();
	const [dismissed, setDismissed] = useState(false);

	const hasUnrecognised = unrecognisedValues.length > 0;

	const hasUnavailable = unavailableComponents.length > 0;

	if (dismissed || (!hasUnrecognised && !hasUnavailable)) {
		return null;
	}

	const dismiss = () => {
		setDismissed(true);
		dismissMissingSummary(configId, section);
		onDismiss();
	};

	const missingNames = unavailableComponents
		.map(item => item.componentName)
		.join(', ');

	return (
		<NotificationBanner
			variant="warning"
			onClose={dismiss}
			sx={{ mb: 3 }}
			dataCy="missing-components-banner">
			{hasUnrecognised && (
				<div>
					<b>
						{t(
							'Some components have values we could not match to the template.',
						)}
					</b>{' '}
					{valuesNeedingScore > 0 && (
						<>
							<span data-cy="values-need-score-count">
								{t('Values that need a score: {{count}}.', {
									count: valuesNeedingScore,
								})}
							</span>{' '}
						</>
					)}
					{t(
						'Open the components marked below and give those values a score, so nothing is left unscored.',
					)}
				</div>
			)}
			{hasUnavailable && (
				<div style={{ marginTop: hasUnrecognised ? 4 : 0 }}>
					<b>
						{t('Not in your data yet: {{names}}.', {
							names: missingNames,
						})}
					</b>{' '}
					{t(
						'Connect the source once available, or remove it from the model.',
					)}{' '}
					<Link
						component="button"
						underline="hover"
						sx={{
							fontSize: 13,
							fontWeight: 700,
							verticalAlign: 'baseline',
						}}
						onClick={onContinue}>
						{t('Continue without them')}
					</Link>
					{' | '}
					<Link
						component="button"
						underline="hover"
						sx={{
							fontSize: 13,
							fontWeight: 700,
							verticalAlign: 'baseline',
						}}
						onClick={onReview}>
						{t('Review')}
					</Link>
				</div>
			)}
		</NotificationBanner>
	);
};

export default MissingComponentsBanner;
